import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Shield, Smartphone, CheckCircle2, Copy, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface MfaSetupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const MfaSetup = ({ open, onOpenChange, onSuccess }: MfaSetupProps) => {
  const [step, setStep] = useState<'generate' | 'verify' | 'success'>('generate');
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [factorId, setFactorId] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateQRCode = async () => {
    setLoading(true);
    try {
      // Get existing factors
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      
      if (existingFactors?.all) {
        // Check for verified factors
        const verifiedFactors = existingFactors.all.filter(f => f.status === 'verified');
        if (verifiedFactors.length > 0) {
          toast.error("Two-factor authentication is already enabled for your account.");
          onOpenChange(false);
          onSuccess();
          return;
        }
        
        // Delete any unverified factors to allow re-enrollment
        const unverifiedFactors = existingFactors.all.filter(f => f.status === 'unverified');
        for (const factor of unverifiedFactors) {
          try {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
            console.log('Removed unverified factor:', factor.id);
          } catch (err) {
            console.error('Failed to remove unverified factor:', err);
          }
        }
      }

      // Now enroll a new factor
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'LoopBaan Authenticator',
        issuer: 'LoopBaan.ai'
      });

      if (error) throw error;

      if (data) {
        // Modify the QR code URI to use LoopBaan.ai as the issuer
        let qrCodeUri = data.totp.qr_code;
        
        // If it's a URI string, replace the issuer
        if (qrCodeUri && !qrCodeUri.startsWith('<svg') && !qrCodeUri.startsWith('data:image')) {
          qrCodeUri = qrCodeUri.replace(
            /otpauth:\/\/totp\/[^:]+:/,
            'otpauth://totp/LoopBaan.ai:'
          ).replace(
            /[?&]issuer=[^&]*/,
            '&issuer=LoopBaan.ai'
          );
        }
        
        setQrCode(qrCodeUri);
        setSecret(data.totp.secret);
        setFactorId(data.id);
        setStep('verify');
      }
    } catch (error: any) {
      console.error('MFA enrollment error:', error);
      toast.error(error.message || "Failed to generate QR code");
      // Allow closing on error
      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verifyCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const challenge = await supabase.auth.mfa.challenge({ factorId });
      
      if (challenge.error) throw challenge.error;

      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code: verifyCode,
      });

      if (verify.error) throw verify.error;

      setStep('success');
      toast.success("Two-factor authentication enabled successfully!");
    } catch (error: any) {
      toast.error(error.message || "Invalid code. Please try again.");
      setVerifyCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    toast.success("Secret copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    // Only allow closing after success
    if (step === 'success') {
      onOpenChange(false);
      onSuccess();
      // Reset state
      setTimeout(() => {
        setStep('generate');
        setQrCode("");
        setSecret("");
        setFactorId("");
        setVerifyCode("");
      }, 300);
    }
    // Cannot close during generate or verify steps to ensure MFA is completed
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" onEscapeKeyDown={(e) => step === 'verify' && e.preventDefault()} onPointerDownOutside={(e) => step === 'verify' && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Set Up Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Add an extra layer of security to your account
          </DialogDescription>
        </DialogHeader>

        {step === 'generate' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Enable 2FA</CardTitle>
              <CardDescription>
                You'll need an authenticator app on your phone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                <Smartphone className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">Compatible authenticator apps:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Google Authenticator</li>
                    <li>Microsoft Authenticator</li>
                    <li>Authy</li>
                    <li>1Password</li>
                  </ul>
                </div>
              </div>
              <Button 
                onClick={generateQRCode} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate QR Code"
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Step 1: Scan QR Code</CardTitle>
                <CardDescription>
                  Open your authenticator app and scan this code
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {qrCode?.trim().startsWith('<svg') ? (
                  <div
                    className="flex justify-center p-4 bg-background rounded-lg"
                    dangerouslySetInnerHTML={{ __html: qrCode }}
                  />
                ) : qrCode?.startsWith('data:image') ? (
                  <div className="flex justify-center p-4 bg-background rounded-lg">
                    <img src={qrCode} alt="2FA QR code for authenticator app" className="h-64 w-64" />
                  </div>
                ) : (
                  <div className="flex justify-center p-4 bg-background rounded-lg">
                    <QRCodeSVG value={qrCode} size={256} level="M" />
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">
                      Can't scan? Enter this key manually:
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={secret}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={handleCopySecret}
                    >
                      {copied ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Step 2: Verify Code</CardTitle>
                <CardDescription>
                  Enter the 6-digit code from your authenticator app
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verifyCode">Authentication Code</Label>
                  <Input
                    id="verifyCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={verifyAndEnable}
                  disabled={loading || verifyCode.length !== 6}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify and Enable 2FA"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 'success' && (
          <Card className="border-green-500/20 bg-green-500/5">
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-center">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg">2FA Enabled Successfully!</h3>
                <p className="text-sm text-muted-foreground">
                  Your account is now protected with two-factor authentication.
                  You'll need to enter a code from your authenticator app each time you log in.
                </p>
              </div>
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};
